import { adminRoot, isAdmin } from 'src/config'
import Model from 'src/models/model'
import ModelIndex from 'src/modules/admin/components/ModelIndex'
import ModelPage from 'src/modules/admin/components/ModelPage'
import { authorizeModelAccess, findModelFromParamName, notifyAccessDenied, redirectIfMissingModelForm, redirectIfMissingModelIndex } from 'src/modules/admin/guards'
import { checkPreviewPermissionsAccess } from 'src/router/guards'

// Lazy loaded Vue Router components should not use defineAsyncComponent()
const ReportComponent = () => import('src/modules/admin/report/Report')

export default ({ app, router }) => {
  if (!isAdmin) return

  /**
   * Set global guards
   */
  router.beforeEach(checkPreviewPermissionsAccess)

  /**
   * Set routes
   */

  const adminRootRoute = {
    path: adminRoot,
    component: () => import('src/modules/admin/layouts/AdminLayout.vue'),
    meta: { auth: { admin: true } }
  }
  adminRootRoute.children = [
    {
      name: 'admin:root',
      path: '',
      redirect: { name: 'admin:dashboard' }
    },
    {
      name: 'admin:dashboard',
      path: 'dashboard',
      component: ReportComponent,
      async beforeEnter (_to, _from, next) {
        await Model.ensureBooted()

        // First check to see if there is a dashboard set
        const isDashboard = Model.context.currentUser?.customDashboard || Model.configuration.currentSite.defaultDashboard || Model.configuration.currentCircuit.defaultDashboard
        if (!isDashboard) {
          next({ name: 'admin:operations-menu' })
          return
        }

        const reportsClass = findModelFromParamName('reports')
        if (!reportsClass || !reportsClass.isViewPermitted) {
          notifyAccessDenied()
          return next({ name: 'admin:operations-menu' })
        }

        return next()
      }
    },
    {
      name: 'admin:modify-membership',
      path: 'modify-membership/:id',
      component: () => import('src/modules/admin/membership/AdminModifyMembership')
    },
    {
      name: 'admin:journal-entry-viewer',
      path: 'operations/:category/journal-entry-viewer',
      component: () => import('src/modules/admin/journal-entry/JournalEntryViewer')
    },
    {
      name: 'admin:report',
      path: 'reports/:category/:id',
      component: ReportComponent
    },
    {
      name: 'admin:settings-menu',
      path: 'settings',
      component: () => import('src/modules/admin/menu/SettingsMenu')
    },
    {
      name: 'admin:settings-category-menu',
      path: 'settings/:category?',
      component: () => import('src/modules/admin/menu/SettingsMenu')
    },
    {
      name: 'admin:operations-menu',
      path: 'operations/:category?',
      component: () => import('src/modules/admin/menu/OperationsMenu')
    },
    {
      name: 'admin:developers-menu',
      path: 'developers/:category?',
      alias: '',
      component: () => import('src/modules/admin/menu/DevelopersMenu')
    },
    {
      name: 'admin:reports-menu',
      path: 'reports/:category?',
      component: () => import('src/modules/admin/menu/ReportsMenu')
    },
    {
      name: 'admin:support-article',
      path: 'support/:id?',
      component: () => import('src/modules/admin/support/SupportArticlesMenu')
    },
    {
      name: 'admin:support-articles-menu',
      path: 'support/:category?',
      component: () => import('src/modules/admin/support/SupportArticlesMenu')
    },
    {
      name: 'admin:site-settings',
      path: 'settings/site/site-settings',
      component: () => import('src/modules/admin/site/SiteSettingsForm')
    },
    {
      name: 'admin:site-theme-settings',
      path: 'settings/website-and-marketing/site-theme',
      component: () => import('src/modules/admin/site/SiteThemeForm')
    },
    {
      name: 'admin:circuit-settings',
      path: 'settings/circuit/circuit-settings',
      component: () => import('src/modules/admin/circuit/CircuitSettingsForm')
    },
    {
      name: 'admin:polaris-settings',
      path: 'settings/integrations/polaris',
      component: () => import('src/modules/admin/integrations/PolarisForm')
    },
    {
      name: 'admin:developer-site-settings',
      path: 'developers/settings/site-settings',
      component: () => import('src/modules/admin/developer/DeveloperSiteSettingsForm')
    },
    {
      name: 'admin:developer-site-stripe-configuration',
      path: 'developers/settings/site-stripe-configuration',
      component: () => import('src/modules/admin/site/SiteStripeConfigurationTable')
    },
    {
      name: 'admin:developer-circuit-settings',
      path: 'developers/settings/circuit-settings',
      component: () => import('src/modules/admin/developer/DeveloperCircuitSettingsForm')
    },
    {
      name: 'admin:developer-feature-flags',
      path: 'developers/settings/feature-flags',
      component: () => import('src/modules/admin/developer/DeveloperFeatureFlagsTable')
    },
    {
      name: 'admin:developer-analytics-events',
      path: 'developers/settings/analytics-events',
      component: () => import('src/modules/admin/developer/DeveloperAnalyticsEventsForm')
    },
    {
      name: 'admin:netlify-status',
      path: 'developers/infrastructure/netlify-status',
      component: () => import('src/modules/admin/developer/NetlifySitesForm')
    },
    {
      name: 'admin:unknown-payments',
      path: 'developers/advanced/unknown-payments',
      component: () => import('src/modules/admin/developer/UnknownPaymentsTable')
    },
    {
      name: 'admin:release-candidates',
      path: 'developers/advanced/release-candidates',
      component: () => import('src/modules/admin/developer/ReleaseCandidates')
    },
    {
      name: 'admin:movie-sorting',
      path: 'settings/programming/movie-sorting',
      component: () => import('src/modules/admin/movie/MovieSortingPage')
    },
    {
      name: 'admin:settings-category-name',
      path: 'settings/:category/:name',
      component: ModelIndex,
      beforeEnter: [redirectIfMissingModelIndex, authorizeModelAccess]
    },
    {
      name: 'admin:operations-category-name',
      path: 'operations/:category/:name',
      component: ModelIndex,
      beforeEnter: [redirectIfMissingModelIndex, authorizeModelAccess]
    },
    {
      name: 'admin:developers-category-name',
      path: 'developers/:category/:name',
      component: ModelIndex,
      beforeEnter: [redirectIfMissingModelIndex, authorizeModelAccess]
    },
    {
      name: 'admin:model-name',
      path: ':name',
      component: ModelIndex,
      beforeEnter: [redirectIfMissingModelIndex, authorizeModelAccess]
    },
    {
      name: 'admin:settings-category-name-id',
      path: 'settings/:category/:name/:id',
      component: ModelPage,
      beforeEnter: [redirectIfMissingModelForm, authorizeModelAccess]
    },
    {
      name: 'admin:operations-category-name-id',
      path: 'operations/:category/:name/:id',
      component: ModelPage,
      beforeEnter: [redirectIfMissingModelForm, authorizeModelAccess]
    },
    {
      name: 'admin:developers-category-name-id',
      path: 'developers/:category/:name/:id',
      component: ModelPage,
      beforeEnter: [redirectIfMissingModelForm, authorizeModelAccess]
    },
    {
      name: 'admin:model-name-id',
      path: ':name/:id',
      component: ModelPage,
      beforeEnter: [redirectIfMissingModelForm, authorizeModelAccess]
    },
    {
      name: 'admin:category-name-id',
      path: ':category/:name/:id',
      component: ModelPage,
      beforeEnter: [redirectIfMissingModelForm, authorizeModelAccess]
    }
  ]

  router.addRoute(adminRootRoute)

  const printReportRoute = {
    name: 'admin:print-report',
    path: '/print_report/:id',
    component: ReportComponent
  }

  router.addRoute(printReportRoute)

  if (isAdmin) {
    app.config.globalProperties.$editModel = function (model, newTab = false, subpath = _.kebabCase(model.collectionVariableName)) {
      if (newTab) {
        window.open(adminRoot + '#/' + subpath + '/' + model.id, '_blank')
      } else {
        router.push(adminRoot + subpath + '/' + model.id)
      }
    }

    router.addRoute({ name: 'app:root', redirect: { name: 'admin:root' } })
  }
}
